import React, { useEffect, useState } from 'react';
import qs from 'query-string';
import { fetchPurchaseOrders } from 'services';
import { DATE_FORMATS, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import { Loading } from '../../../components/loading/Loading';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import Filter from './Filter';
import _ from 'lodash';
import Currency from 'components/Currency/Currency';
import CustomLink from 'components/TableLink';
import DataTable from 'components/DataTable';

let filters: { [p: string]: any; page: number } = {
  page: 0,
};

let columns = [
  {
    name: 'PurchaseID',
    selector: 'objectId',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.objectId}
        linkTo={`/purchase-orders/${row.objectId}`}
      />
    ),
  },
  {
    name: 'Title',
    selector: 'title',
  },
  {
    name: 'Ordered At',
    selector: 'createdAt',
    format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
  },
  {
    name: 'Supplier Name',
    selector: 'supplier.name',
  },
  {
    name: 'Status',
    selector: 'status',
  },
  {
    name: 'Total',
    selector: 'subtotal',
    // eslint-disable-next-line react/display-name
    format: row => row.subtotal ? (<Currency amount={parseFloat(row.subtotal?.toFixed(2))} default={'-'} />) : '-',
  },
];

const PurchaseOrders = props => {
  const { match: { path, params: { supplierId }}} = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [purchaseOrders, setPurchaseOrders] = useState<Record<string, any>>({});
  const isSupplierPage = (path ==='/suppliers/:supplierId');

  if(isSupplierPage){
    columns = columns.filter(col=> col.selector !== 'supplier.name');
  }
  
  const fetchPurchaseOrdersFn = async () => {
    setLoading(true);
    let { page, ...rest } = filters;
    if(isSupplierPage){
      rest = { ...rest , supplierId };
    }
    try {
      const response = await fetchPurchaseOrders({
        page,
        limit: getPageLimit(),
        filter: { ...rest },
      });
      setPurchaseOrders(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    if(!isSupplierPage){
      filters = {
        ...filters,
        ...queryStringToFilter(),
      };
    }
    fetchPurchaseOrdersFn();
  }, []);

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    if(!isSupplierPage){
      updateQueryString();
    }
    fetchPurchaseOrdersFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    if (updatedFilter.POId) {
      props.history.push(`/purchase-orders/${updatedFilter.POId}`);
    } else {
      filters = { ...updatedFilter, page: 0 };
      if(!isSupplierPage){
        updateQueryString();
      }
      fetchPurchaseOrdersFn();
    }
  };

  return (
    <>
      {/* {loading ? <Loading /> : null} */}
      <div className={isSupplierPage ? styles['sub-header'] : styles['header']}>
        <div className={isSupplierPage ? styles['sub-heading'] : styles['heading']}>Purchase Orders</div>
        {!isSupplierPage &&
          <ActionButton
            text={<>Purchase Order <span>+</span></>}
            active={true}
            isLoading={false}
            onClick={() => props.history.push('/purchase-orders/create')}
          />
        }
      </div>
      <Filter {...props} onSubmit={onFilterSubmit} />
      <DataTable
        keyField="objectId"
        columns={columns}
        noHeader={true}
        data={purchaseOrders.items}
        highlightOnHover={true}
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={purchaseOrders.total}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        loading={loading}
      />
    </>
  );
};

export { PurchaseOrders };
